<template>
  <module-layout class="page-module">
    <Loader v-if="loading"></Loader>
    <section class="module-viewer" v-else>

      <span class="mobile-menu-toggle" @click="isMobileSidebarOpen = !isMobileSidebarOpen">
        <div class="menu-icon" :class="{ 'open': isMobileSidebarOpen }"><span></span><span></span><span></span></div>
      </span>

      <div class="module-sidebar" :class="{ 'is-open': isMobileSidebarOpen }">
        <div class="module-data">
          <router-link class="back" :to="{ name: 'course', params: { courseId: $route.params.courseId, langId: $route.params.langId } }">&#9664; Back</router-link>
          <!-- <div class="languageButtons" v-if="$route.params.courseId == 1">
            <Button
              :onClick="() => this.loadLanguage(1)"
              :type="$route.params.langId == 1 ? 'primary':'secondary'"
              size="small">EN</Button>
            <Button size="small"
              :onClick="() => this.loadLanguage(2)"
              :type="$route.params.langId == 2 ? 'primary':'secondary'">FI</Button>
          </div> -->
          <div class="module-number">Module {{ module.order + 1 }}</div>
          <div class="module-title">{{ module.name }}</div>
          <progress-bar :max="module.lessons.length || 100" :progress="progress.currentLesson" :theme="'white'" :height="4" v-if="progress"></progress-bar>
        </div>

        <div class="module-nav" v-if="module">
          <div class="module-nav-item" :class="getLessonStatus(lesson)" v-for="lesson in module.lessons" :key="`lesson-${lesson.id}`">
            <router-link :to="{ name: 'lesson', params: { courseId: $route.params.courseId, langId: $route.params.langId, moduleId: $route.params.moduleId, lessonId: lesson.id }}">{{ lesson.order + 1 }}. {{ lesson.title }}</router-link>
          </div>
          <template v-if="module.resources && module.resources.length">
            <hr>
            <h3 class="module-nav-title">Module materials</h3>
            <div class="module-nav-item" v-for="resource in module.resources" :key="`resource-${resource.id}`">
              <a :href="resource.url" target="_blank">
                <span class="icon" :style="`background-image: url(${resource.type.img});`"></span>
                {{ resource.title }}
              </a>
            </div>
          </template>
        </div>
      </div>
      <div class="module-content">
        <LessonContent :lesson="lesson"></LessonContent>
        <FinishLessonButton
          :name="`${nextLesson.order + 1}. ${nextLesson.title}`"
          :isLastModule="activeModuleIsLast"
          :isLastLesson="activeLessonIsLast"
          @finishLesson="onFinishLesson"
          v-if="nextLesson"></FinishLessonButton>
      </div>
    </section>
  </module-layout>
</template>

<script>
import CourseService from '@/services/CourseService'

import ModuleLayout from '@/layouts/ModuleLayout'
import LessonContent from '@/components/LessonContent'
import ProgressBar from '@/components/ProgressBar'
import Loader from '@/components/Loader'
import FinishLessonButton from '@/components/FinishLessonButton'
import Button from '@/components/Button'

import { mapState } from 'vuex'

export default {
  components: {
    ModuleLayout,
    LessonContent,
    ProgressBar,
    Button,
    Loader,
    FinishLessonButton
  },

  watch: {
    '$route': 'fetchLesson'
  },

  computed: {
    ...mapState('course', {
      progress: state => state.progress,
      activeModule: state => state.activeModule,
      activeLesson: state => state.activeLesson
    }),

    moduleIsCompleted () {
      if (!this.progress || !this.progress.modules.length) return false
      return this.progress.modules.find(module => module.id === parseInt(this.$route.params.moduleId)).status === 'completed'
    },

    activeModuleIsLast () {
      return this.activeModule.order === this.progress.modules.length - 1
    },

    activeLessonIsLast () {
      return this.activeLesson.order === this.activeModule.lessons.length - 1
    },

    nextLesson () {
      if (!this.module || !this.activeLesson) return null
      return this.$store.getters['course/nextLesson'](this.module.order, this.activeLesson.order)
    }
  },

  methods: {
    fetchProgress () {
      const progress = this.$store.state.course.progress
      if (progress.modules.length) {
        return Promise.resolve(progress)
      } else {
        this.$store.dispatch('course/setUserId', this.$store.state.user.id)
        this.$store.dispatch('course/setCourseId', this.$route.params.courseId)
        return this.$store.dispatch('course/getProgress')
          .then(progress => {
            return progress
          })
      }
    },

    fetchModule () {
      return CourseService.getModule(this.$route.params.courseId, this.$route.params.langId, this.$route.params.moduleId)
        .then(module => {
          this.module = module
          this.$store.commit('course/setActiveModule', module)
          return module
        })
    },

    fetchLesson () {
      // Update lessonId, since it won't be updated if we manually change the route
      // after data() is created
      this.lessonId = parseInt(this.$route.params.lessonId)

      this.isMobileSidebarOpen = false

      return CourseService.getLesson(this.$route.params.lessonId, this.$route.params.langId)
        .then((lesson) => {
          this.lesson = lesson
          this.$store.commit('course/setActiveLesson', lesson)
          return lesson
        }, (error) => {
          if (error.response.status === 404) {
            this.$router.push({ name: 'course', params: { courseId: this.$route.params.courseId, langId: this.$route.params.langId } })
          }
        })
    },

    getLessonStatus (lesson) {
      if (!this.activeLesson) return ''

      if (lesson.order === this.activeLesson.order) return 'active'
      else if (this.moduleIsCompleted) return 'completed'
      else if (lesson.order <= this.progress.currentLesson) return 'completed'
      else return 'locked'
    },

    onFinishLesson () {
      // let progress = JSON.parse(JSON.stringify(this.progress))
      let activeModule = this.progress.modules.find(module => module.id === this.$route.params.moduleId)
      let activeLesson = activeModule.lessons.find(lesson => lesson.id === this.lessonId)

      // If it's the last lesson in a module
      if (activeLesson.order === activeModule.lessons.length - 1) {
        // If the module wasn't already completed, mark it as completed
        if (activeModule.status !== 'completed') {
          activeModule.status = 'completed'

          // If it's not the last module of the course, also unlock next module and set
          // nextLesson to its first module
          if (activeModule.order < this.progress.modules.length - 1) {
            this.progress.currentModule += 1
            this.progress.currentLesson = 0
            this.progress.modules[this.progress.currentModule].status = 'unlocked'

            // Update nextLesson object. We don't do this for the last module of the course.
            activeModule.nextLesson = this.progress.modules[this.progress.currentModule].lessons[0]
          }
        }

        // In any case, go back to course page
        this.$store.dispatch('course/setProgress', this.progress).then(() => {
          this.$router.push({ name: 'course', params: { courseId: this.$route.params.courseId, langId: this.$route.params.langId } })
        })

      // Else (if it's not the last lesson in the module)
      } else {
        // If it's the last unlocked lesson, and if it belongs to the last unlocked module,
        // then just set nextLesson to the next lesson of the module
        if (activeLesson.order === this.progress.currentLesson &&
            activeModule.order === this.progress.currentModule) {
          this.progress.currentLesson += 1
          activeModule.nextLesson = this.progress.modules[this.progress.currentModule].lessons[this.progress.currentLesson]
        }

        // In any other case, it means the user was in an already completed lesson, so we just
        // take him to the next lesson
        this.$store.dispatch('course/setProgress', this.progress).then(() => {
          const nextLesson = activeModule.lessons[activeLesson.order + 1]
          this.$router.push({ name: 'lesson', params: { courseId: this.$route.params.courseId, langId: this.$route.params.langId, moduleId: activeModule.id, lessonId: nextLesson.id } })
        })
      }
    },
    getModuleInfo () {
      Promise.all([
        this.fetchProgress(),
        this.fetchModule(),
        this.fetchLesson()
      ]).then(() => {
        this.loading = false
      })
    },
    loadLanguage (langId) {
      this.$router.push({
        name: 'lesson',
        params: {
          courseId: this.$route.params.courseId,
          moduleId: this.$route.params.moduleId,
          lessonId: this.$route.params.lessonId,
          langId: langId}},
      this.getModuleInfo)
    }
  },

  created () {
    this.getModuleInfo()
  },

  data () {
    return {
      loading: true,
      isMobileSidebarOpen: false,
      module: {},
      lesson: {}
    }
  }
}
</script>

<style lang="scss">

@import '~styles/base/variables';
@import '~styles/base/mixins';

.page-module {

  .languageButtons {
      margin-top: 15px;
    button.button {
      margin-right:5px;
    }
  }

  .mobile-menu-toggle {
    height: 60px;
    display: flex;
    align-items: center;
    border-left: none;
    cursor: pointer;
  }

  .mobile-menu-toggle {
    display: none;
  }
}

.module-viewer {
  display: flex;
}

.module-content {
  max-width: 40rem;
  margin: 0 auto;
  padding: 5rem 1rem;
}

.module-sidebar {
  display: flex;
  flex-direction: column;
  flex: 0 0 300px;
  max-width: 300px;
  background-color: $color-white;
  border-right: 1px solid $color-gray-light;

  .back {
    @include text-size-xs();
    font-weight: $font-weight-semibold;
    color: $color-white;
    text-transform: uppercase;
  }

  .module-data {
    padding: 1.5rem;
    color: $color-white;
    background-color: $color-text-dark;
  }

  .module-number {
    margin-top: 2rem;
    @include text-size-s();
    font-weight: $font-weight-semibold;
    color: rgba($color-white, .56);
    text-transform: uppercase;
  }

  .module-title {
    margin-bottom: 2rem;
    @include text-size-xxl();
    font-weight: $font-weight-semibold;
  }
}

.module-nav-title {
  padding: 0 1rem 0 2rem;
  font-weight: $font-weight-semibold;
  @include text-size-s();
  color: $color-text-dark;
  text-transform: uppercase;
}

.module-nav-item {

  > a {
    display: flex;
    align-items: center;
    padding: 1.5rem 1rem 1.5rem 2rem;
    font-weight: $font-weight-semibold;
    @include text-size-m();
    color: $color-text-dark;
    word-break: break-word;

    .icon {
      align-self: flex-start;
      flex: 0 0 auto;
      margin-right: .5rem;
    }
  }

  &.active {
    background-color: $color-gray-lighter;
    box-shadow: inset 5px 0 0 0 $color-brand-main;
  }

  &.locked > a {
    cursor: not-allowed;
    pointer-events: none;
    color: $color-text-light;
  }

  &:not(.locked):hover{
    background-color: $color-gray-lighter;
  }
}

@media screen and (max-width: 768px) {

  .page-module {

    .mobile-menu-toggle {
      display: flex;
    }

    .mobile-menu-toggle {
      position: fixed;
      top: 0;
      right: 0;
      width: 4rem;
      max-width: 20vw;
      z-index: 2;
      background-image: linear-gradient(215deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%);
    }
  }

  .module-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    height: 100vh;
    width: 80vw;
    max-width: 20rem;
    box-shadow: 2px 0 10px 0 rgba(#e4e5e9, .7);
    z-index: 1;
    transform: translateX(-100%);
    transition: transform .15s ease-in-out;

    &.is-open {
      transform: translateX(0);
    }
  }

  .module-nav {
    overflow-y: auto;
  }
}

.menu-icon {
  width: 26px;
  height: 22px;
  position: relative;
  margin: 0 auto;
  transform: rotate(0deg);
  transition: .5s ease-in-out;
  cursor: pointer;

  span {
    display: block;
    position: absolute;
    height: 4px;
    width: 100%;
    background: #6b7285;
    border-radius: 2px;
    opacity: 1;
    left: 0;
    transform: rotate(0deg);
    transition: .25s ease-in-out;
    transform-origin: left center;
  }

  span:nth-child(1) { top: 0; }
  span:nth-child(2) { top: 50%; transform: scaleX(1) translateY(-50%); }
  span:nth-child(3) { bottom: 0; }

  &.open span:nth-child(1) {
    transform: rotate(45deg);
  }

  &.open span:nth-child(2) {
    transform: scaleX(0) translateY(-50%);
    opacity: 0;
  }

  &.open span:nth-child(3) {
    transform: rotate(-45deg);
  }
}

</style>



// WEBPACK FOOTER //
// src/pages/ModulePage.vue